import { lazy } from 'react';
import type { ComponentType } from 'react';

/**
 * Handles the "Failed to fetch dynamically imported module" error that happens
 * after a new deploy: the user still has the previous index.html in memory and
 * tries to lazy-load a chunk hash that no longer exists on the server. The fix
 * is to force a one-time full reload so the browser fetches the fresh
 * index.html (and the new asset manifest).
 */

const RELOAD_KEY = 'chunkReloadAt';
// Only allow one automatic reload per this window to avoid reload loops when a
// chunk genuinely can't load (offline, a real 404, etc.).
const RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
];

export function isChunkLoadError(message?: string | null): boolean {
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function canReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    return Date.now() - last > RELOAD_COOLDOWN_MS;
  } catch {
    return true;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable — best effort only */
  }
}

/** Reload at most once per cooldown window. Returns true if a reload was triggered. */
export function reloadForChunkError(): boolean {
  if (!canReload()) return false;
  markReloaded();
  window.location.reload();
  return true;
}

/**
 * Installs global listeners that recover from stale-chunk errors after a deploy.
 * Call once during app bootstrap.
 */
export function installChunkErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  // Vite dispatches this when a dynamically imported module fails to preload.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadForChunkError();
  });

  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event?.message) || isChunkLoadError(event?.error?.message)) {
      reloadForChunkError();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message = typeof reason === 'string' ? reason : reason?.message;
    if (isChunkLoadError(message)) {
      reloadForChunkError();
    }
  });
}

/**
 * Wraps a dynamic import() (e.g. inside a React Router `lazy:` route) so a
 * stale-chunk error after a deploy triggers a one-time reload instead of a
 * broken route.
 */
export async function importWithReload<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory();
  } catch (error: any) {
    if (isChunkLoadError(error?.message) && reloadForChunkError()) {
      return await new Promise<T>(() => {});
    }
    throw error;
  }
}

/**
 * Drop-in replacement for React.lazy that recovers from stale-chunk errors by
 * forcing a one-time reload instead of surfacing a broken page.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error: any) {
      if (isChunkLoadError(error?.message) && reloadForChunkError()) {
        // A reload is underway; never resolve so we don't flash the error
        // boundary before the page navigates away.
        return await new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
